import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import panamaLocations4 from "../data/panamaLocations4.json";

function normalize(s) {
  return String(s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}


function locationAliases(item) {
  if (!item || typeof item !== "object") return [];
  return Array.isArray(item.aliases) ? item.aliases.filter(Boolean) : [];
}

function locationSearchText(parts = []) {
  return parts
    .flatMap((part) => Array.isArray(part) ? part : [part])
    .filter(Boolean)
    .map(normalize)
    .join(" ");
}

function uniqueLocationHits(rows = []) {
  const seen = new Set();
  const out = [];
  rows.forEach((row) => {
    const key = [row.level, row.province, row.district, row.corregimiento, row.zone].filter(Boolean).join("|").toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(row);
  });
  return out;
}

export const USER_LOCATION_STORAGE_KEY = "loop_user_location_pa_v1";

export function buildLocationLabel(loc) {
  if (!loc) return "";
  const parts = [loc.zone, loc.corregimiento, loc.district, loc.province, "Panamá"].filter(Boolean);
  return parts.join(", ");
}

export function getLocationLevel(loc) {
  if (!loc) return "";
  if (loc.zone) return "zone";
  if (loc.corregimiento) return "corregimiento";
  if (loc.district) return "district";
  if (loc.province) return "province";
  return "";
}

export function buildCoverageLabel(loc) {
  if (!loc) return "";
  const level = loc.level || getLocationLevel(loc);
  if (level === "province") return loc.province || loc.label || "";
  if (level === "district") return [loc.district, loc.province].filter(Boolean).join(", ");
  if (level === "corregimiento") return [loc.corregimiento, loc.district, loc.province].filter(Boolean).join(", ");
  return [loc.zone, loc.corregimiento, loc.district, loc.province].filter(Boolean).join(", ");
}

export function readUserLocation() {
  try {
    const raw = localStorage.getItem(USER_LOCATION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (parsed.country && parsed.country !== "PA") return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeUserLocation(loc) {
  try {
    if (!loc) {
      localStorage.removeItem(USER_LOCATION_STORAGE_KEY);
      return;
    }
    const safe = {
      country: "PA",
      province: loc.province || null,
      district: loc.district || null,
      corregimiento: loc.corregimiento || null,
      zone: loc.zone || null,
      level: loc.level || getLocationLevel(loc),
      label: loc.label || buildLocationLabel(loc),
      updatedAt: Date.now(),
    };
    localStorage.setItem(USER_LOCATION_STORAGE_KEY, JSON.stringify(safe));
  } catch {}
}

function parseInitialLabelToValue(initialLabel) {
  const raw = String(initialLabel || "").trim();
  if (!raw) return null;

  const parts = raw.split(",").map((p) => p.trim()).filter(Boolean);
  const cleaned = parts.filter((p) => {
    const n = normalize(p);
    return n !== "panama" && n !== "panamá";
  });

  const zone = cleaned[0] || null;
  const corregimiento = cleaned[1] || null;
  const district = cleaned[2] || null;
  const province = cleaned[3] || null;

  if (!province && !district && !corregimiento && !zone) return null;

  return {
    country: "PA",
    province,
    district,
    corregimiento,
    zone,
    level: getLocationLevel({ province, district, corregimiento, zone }),
    label: buildLocationLabel({ province, district, corregimiento, zone }),
  };
}

function money(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n) || n <= 0) return "$0";
  return `$${n.toFixed(n % 1 === 0 ? 0 : 2)}`;
}

export default function PanamaLocationPickerModal({
  open,
  onClose,
  initialValue = null,
  initialLabel = "",
  requireAllLevels = true,
  allowPartialSelection = false,
  coverageMode = false,
  showExtraFee = false,
  initialExtraFee = 0,
  onConfirm,
  title = "Ubicación",
  subtitle = "",
  confirmLabel = "Continuar",
}) {
  const data = useMemo(() => (Array.isArray(panamaLocations4) ? panamaLocations4 : []), []);

  const [locationQuery, setLocationQuery] = useState("");
  const [selectedProvince, setSelectedProvince] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedCorregimiento, setSelectedCorregimiento] = useState(null);
  const [selectedZone, setSelectedZone] = useState(null);
  const [extraFee, setExtraFee] = useState(initialExtraFee || 0);

  useEffect(() => {
    if (!open) return;

    const fromObj = initialValue && typeof initialValue === "object" ? initialValue : null;
    const fromLabel = !fromObj ? parseInitialLabelToValue(initialLabel) : null;
    const v = fromObj || fromLabel || null;

    setSelectedProvince(v?.province || null);
    setSelectedDistrict(v?.district || null);
    setSelectedCorregimiento(v?.corregimiento || null);
    setSelectedZone(v?.zone || null);
    setExtraFee(Number(v?.extraFee ?? initialExtraFee ?? 0) || 0);

    // Important UX change:
    // do not pre-fill the search box with the full old label.
    // That old behavior made changing province/district feel like you had to delete everything first.
    setLocationQuery("");
  }, [open, initialValue, initialLabel, initialExtraFee]);

  const allHits = useMemo(() => {
    const q = normalize(locationQuery);
    if (!q) return [];

    const hits = [];
    for (const p of data) {
      const province = p?.province || "";
      const provinceAliases = locationAliases(p);
      if (locationSearchText([province, provinceAliases]).includes(q)) {
        hits.push({ level: "province", province, label: province });
      }

      for (const d of Array.isArray(p?.districts) ? p.districts : []) {
        const district = d?.name || "";
        const districtAliases = locationAliases(d);
        if (locationSearchText([district, districtAliases, province]).includes(q)) {
          hits.push({ level: "district", province, district, label: `${district}, ${province}` });
        }

        for (const c of Array.isArray(d?.corregimientos) ? d.corregimientos : []) {
          const corregimiento = typeof c === "string" ? c : c?.name || "";
          const corregimientoAliases = typeof c === "object" ? locationAliases(c) : [];
          if (locationSearchText([corregimiento, corregimientoAliases, district, province]).includes(q)) {
            hits.push({ level: "corregimiento", province, district, corregimiento, label: `${corregimiento}, ${district}, ${province}` });
          }

          for (const zone of Array.isArray(c?.zones) ? c.zones : []) {
            const zoneLabel = typeof zone === "string" ? zone : zone?.name || zone?.label || "";
            const zoneAliases = typeof zone === "object" ? locationAliases(zone) : [];
            const fullLabel = `${zoneLabel}, ${corregimiento}, ${district}, ${province}`;
            if (locationSearchText([zoneLabel, zoneAliases, corregimiento, district, province, fullLabel]).includes(q)) {
              hits.push({ level: "zone", province, district, corregimiento, zone: zoneLabel, label: fullLabel });
            }
          }
        }
      }
    }

    return uniqueLocationHits(hits).slice(0, 32);
  }, [locationQuery, data]);

  const provinces = useMemo(() => data.map((p) => p.province).filter(Boolean), [data]);

  const districtObjects = useMemo(() => {
    if (!selectedProvince) return [];
    const provObj = data.find((p) => p && p.province === selectedProvince);
    return Array.isArray(provObj?.districts) ? provObj.districts : [];
  }, [selectedProvince, data]);

  const districtNames = useMemo(() => districtObjects.map((d) => d.name).filter(Boolean), [districtObjects]);

  const corregimientoObjects = useMemo(() => {
    if (!selectedDistrict) return [];
    const distObj = districtObjects.find((d) => d && d.name === selectedDistrict);
    return Array.isArray(distObj?.corregimientos) ? distObj.corregimientos : [];
  }, [districtObjects, selectedDistrict]);

  const corregimientoNames = useMemo(() => corregimientoObjects.map((c) => (typeof c === "string" ? c : c?.name)).filter(Boolean), [corregimientoObjects]);

  const zoneNames = useMemo(() => {
    if (!selectedCorregimiento) return [];
    const corrObj = corregimientoObjects.find((c) => typeof c === "string" ? c === selectedCorregimiento : c?.name === selectedCorregimiento);
    return corrObj && typeof corrObj === "object" && Array.isArray(corrObj.zones) ? corrObj.zones.filter(Boolean) : [];
  }, [corregimientoObjects, selectedCorregimiento]);

  const selectedLoc = useMemo(() => {
    const loc = {
      country: "PA",
      province: selectedProvince || null,
      district: selectedDistrict || null,
      corregimiento: selectedCorregimiento || null,
      zone: selectedZone || null,
    };
    const level = getLocationLevel(loc);
    return {
      ...loc,
      level,
      label: coverageMode ? buildCoverageLabel({ ...loc, level }) : buildLocationLabel(loc),
      extraFee: Number(extraFee || 0) || 0,
    };
  }, [selectedProvince, selectedDistrict, selectedCorregimiento, selectedZone, coverageMode, extraFee]);

  const selectedLabel = selectedLoc.label || "—";

  if (!open) return null;

  const hasAnySelection = Boolean(selectedProvince);
  const hasFullSelection = Boolean(selectedProvince && selectedDistrict && selectedCorregimiento && selectedZone);
  const isValid = requireAllLevels && !allowPartialSelection ? hasFullSelection : hasAnySelection;

  const applyHit = (hit) => {
    setSelectedProvince(hit.province || null);
    setSelectedDistrict(hit.district || null);
    setSelectedCorregimiento(hit.corregimiento || null);
    setSelectedZone(hit.zone || null);
    setLocationQuery("");
  };

  const clearSelection = () => {
    setSelectedProvince(null);
    setSelectedDistrict(null);
    setSelectedCorregimiento(null);
    setSelectedZone(null);
    setLocationQuery("");
  };

  const confirm = () => {
    onConfirm && onConfirm(selectedLoc);
  };

  const column = (label, items, selected, onClick, emptyText) => (
    <div className="pa-loc-column">
      <div className="pa-loc-column-head">{label}</div>
      <div className="pa-loc-list">
        {items.length === 0 ? (
          <div className="pa-loc-empty">{emptyText}</div>
        ) : items.map((item) => {
          const active = item === selected;
          return (
            <button
              key={item}
              type="button"
              className={`pa-loc-row ${active ? "active" : ""}`}
              onClick={() => onClick(item)}
            >
              <span>{item}</span>
              <em>{active ? "✓" : "›"}</em>
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="pa-loc-overlay" onClick={() => onClose && onClose()}>
      <style>{styles}</style>
      <div className="pa-loc-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pa-loc-head">
          <div>
            <div className="pa-loc-kicker">Panamá</div>
            <h3>{title}</h3>
            <p>{subtitle || (coverageMode ? "Selecciona hasta qué nivel atiende el negocio: provincia, distrito, corregimiento o zona." : "Selecciona la ubicación principal del negocio.")}</p>
          </div>
          <button type="button" className="pa-loc-close" onClick={() => onClose && onClose()} aria-label="Cerrar">✕</button>
        </div>

        <div className="pa-loc-search-wrap">
          <input
            value={locationQuery}
            onChange={(e) => setLocationQuery(e.target.value)}
            placeholder="Buscar zona, corregimiento, distrito o provincia..."
            className="pa-loc-search"
            autoFocus
          />
          {locationQuery ? (
            <div className="pa-loc-results">
              {allHits.length === 0 ? (
                <div className="pa-loc-result-empty">Sin resultados. Prueba escribir el barrio o zona, por ejemplo: Condado del Rey, Brisas del Golf, El Dorado o Costa del Este.</div>
              ) : allHits.map((hit, idx) => (
                <button type="button" key={`${hit.label}-${idx}`} className="pa-loc-result" onClick={() => applyHit(hit)}>
                  <strong>{hit.label}</strong>
                  <span>{hit.level === "zone" ? "Zona" : hit.level === "corregimiento" ? "Corregimiento" : hit.level === "district" ? "Distrito" : "Provincia"}</span>
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <div className="pa-loc-selected-card">
          <div>
            <span>Seleccionado</span>
            <strong>{selectedLabel}</strong>
            {coverageMode ? <small>Nivel: {selectedLoc.level || "—"}</small> : null}
          </div>
          <button type="button" onClick={clearSelection}>Cambiar desde cero</button>
        </div>

        <div className="pa-loc-grid">
          {column("Provincia", provinces, selectedProvince, (p) => {
            setSelectedProvince(p);
            setSelectedDistrict(null);
            setSelectedCorregimiento(null);
            setSelectedZone(null);
          }, "Sin provincias")}

          {column("Distrito", districtNames, selectedDistrict, (d) => {
            setSelectedDistrict(d);
            setSelectedCorregimiento(null);
            setSelectedZone(null);
          }, selectedProvince ? "Sin distritos" : "Elige provincia")}

          {column("Corregimiento", corregimientoNames, selectedCorregimiento, (c) => {
            setSelectedCorregimiento(c);
            setSelectedZone(null);
          }, selectedDistrict ? "Sin corregimientos" : "Elige distrito")}

          {column("Zona", zoneNames, selectedZone, (z) => setSelectedZone(z), selectedCorregimiento ? "Puedes continuar sin zona exacta" : "Elige corregimiento")}
        </div>

        {coverageMode && showExtraFee ? (
          <div className="pa-loc-fee">
            <div>
              <strong>Costo adicional para esta zona</strong>
              <span>Es un costo fijo por atención en esa zona, no por hora.</span>
            </div>
            <label>
              <em>$</em>
              <input
                type="number"
                min="0"
                step="0.01"
                value={extraFee}
                onChange={(e) => setExtraFee(e.target.value)}
              />
            </label>
          </div>
        ) : null}

        <div className="pa-loc-foot">
          <div className="pa-loc-helper">
            {coverageMode
              ? "Puedes guardar cobertura amplia, por ejemplo toda una provincia o solo una zona específica."
              : "Para local físico recomendamos seleccionar zona exacta, pero ahora cambiar ubicación es más fácil."}
          </div>
          <div className="pa-loc-actions">
            <button type="button" className="pa-loc-secondary" onClick={() => onClose && onClose()}>Cancelar</button>
            <button type="button" className="pa-loc-primary" disabled={!isValid} onClick={confirm}>
              {confirmLabel}{showExtraFee ? ` · ${money(extraFee)}` : ""}
            </button>
          </div>
        </div>

        {data.length === 0 ? (
          <div className="pa-loc-error">No se pudo cargar panamaLocations4.json.</div>
        ) : null}
      </div>
    </div>
  );
}

PanamaLocationPickerModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  initialValue: PropTypes.object,
  initialLabel: PropTypes.string,
  requireAllLevels: PropTypes.bool,
  allowPartialSelection: PropTypes.bool,
  coverageMode: PropTypes.bool,
  showExtraFee: PropTypes.bool,
  initialExtraFee: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onConfirm: PropTypes.func,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  confirmLabel: PropTypes.string,
};

const styles = `
.pa-loc-overlay{position:fixed;inset:0;z-index:100050;background:rgba(15,23,42,.54);padding:20px;overflow:auto}
.pa-loc-modal{width:min(1180px,100%);margin:4vh auto;background:#fff;border-radius:30px;box-shadow:0 30px 90px rgba(15,23,42,.30);overflow:hidden}
.pa-loc-head{padding:22px 24px;border-bottom:1px solid #eef2f7;display:flex;align-items:flex-start;justify-content:space-between;gap:16px;background:linear-gradient(180deg,#fff,#f8fafc)}
.pa-loc-kicker{color:#00675f;font-size:11px;font-weight:950;letter-spacing:.16em;text-transform:uppercase;margin-bottom:8px}
.pa-loc-head h3{margin:0;color:#0f172a;font-size:30px;line-height:1;font-weight:950;letter-spacing:-.055em}
.pa-loc-head p{margin:9px 0 0;color:#64748b;font-size:14px;line-height:1.45;font-weight:700;max-width:720px}
.pa-loc-close{width:42px;height:42px;border-radius:999px;border:1px solid #e2e8f0;background:#fff;color:#64748b;font-weight:950;cursor:pointer}
.pa-loc-search-wrap{position:relative;padding:16px 24px 0}
.pa-loc-search{width:100%;height:52px;border-radius:18px;border:1px solid #e2e8f0;background:#fff;padding:0 16px;font-size:15px;font-weight:750;box-sizing:border-box;outline:none}
.pa-loc-search:focus{border-color:rgba(0,103,95,.38);box-shadow:0 0 0 4px rgba(0,103,95,.10)}
.pa-loc-results{position:absolute;left:24px;right:24px;top:76px;z-index:4;background:#fff;border:1px solid #e2e8f0;border-radius:18px;box-shadow:0 18px 44px rgba(15,23,42,.16);max-height:320px;overflow:auto}
.pa-loc-result{width:100%;border:0;background:#fff;border-bottom:1px solid #f1f5f9;padding:12px 14px;text-align:left;display:flex;align-items:center;justify-content:space-between;gap:12px;cursor:pointer}
.pa-loc-result strong{color:#0f172a;font-size:13px;font-weight:900}
.pa-loc-result span{color:#00675f;background:#ecfdf5;border-radius:999px;padding:5px 8px;font-size:11px;font-weight:950}
.pa-loc-result-empty{padding:14px;color:#94a3b8;font-weight:850}
.pa-loc-selected-card{margin:14px 24px 0;border:1px solid rgba(0,103,95,.18);background:#f0fdfa;border-radius:22px;padding:14px;display:flex;align-items:center;justify-content:space-between;gap:14px}
.pa-loc-selected-card span{display:block;color:#64748b;font-size:11px;font-weight:950;text-transform:uppercase;letter-spacing:.08em}
.pa-loc-selected-card strong{display:block;color:#0f172a;font-size:15px;font-weight:950;margin-top:4px}
.pa-loc-selected-card small{display:block;color:#00675f;font-size:12px;font-weight:850;margin-top:3px}
.pa-loc-selected-card button{border:1px solid #99f6e4;background:#fff;color:#00675f;border-radius:999px;min-height:36px;padding:0 12px;font-size:12px;font-weight:950;cursor:pointer}
.pa-loc-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:12px;padding:16px 24px}
.pa-loc-column{border:1px solid #e5e7eb;border-radius:20px;overflow:hidden;background:#fff;min-width:0}
.pa-loc-column-head{padding:12px 14px;font-weight:950;color:#0f172a;background:#f8fafc;border-bottom:1px solid #e5e7eb}
.pa-loc-list{max-height:310px;overflow:auto}
.pa-loc-row{width:100%;border:0;border-bottom:1px solid #f1f5f9;background:#fff;color:#0f172a;display:flex;align-items:center;justify-content:space-between;gap:8px;text-align:left;padding:11px 13px;cursor:pointer}
.pa-loc-row span{font-size:13px;font-weight:800;line-height:1.25}
.pa-loc-row em{font-style:normal;color:#94a3b8;font-weight:950}
.pa-loc-row.active{background:#00675f;color:#fff}
.pa-loc-row.active em{color:#fff}
.pa-loc-empty{padding:13px;color:#94a3b8;font-size:13px;font-weight:800}
.pa-loc-fee{margin:0 24px 16px;border:1px solid #e2e8f0;background:#f8fafc;border-radius:22px;padding:14px;display:flex;align-items:center;justify-content:space-between;gap:16px}
.pa-loc-fee strong{display:block;color:#0f172a;font-size:14px;font-weight:950}
.pa-loc-fee span{display:block;color:#64748b;font-size:12px;font-weight:750;margin-top:4px}
.pa-loc-fee label{width:150px;height:46px;border:1px solid #e2e8f0;background:#fff;border-radius:16px;display:flex;align-items:center;gap:4px;padding:0 12px;box-sizing:border-box}
.pa-loc-fee label em{font-style:normal;color:#00675f;font-weight:950}
.pa-loc-fee input{width:100%;border:0;outline:none;font-size:16px;font-weight:950;color:#0f172a}
.pa-loc-foot{padding:16px 24px 22px;border-top:1px solid #eef2f7;display:flex;align-items:center;justify-content:space-between;gap:16px;background:#fff}
.pa-loc-helper{color:#64748b;font-size:12px;line-height:1.4;font-weight:750}
.pa-loc-actions{display:flex;gap:10px}
.pa-loc-secondary,.pa-loc-primary{min-height:44px;border-radius:16px;padding:0 16px;font-size:13px;font-weight:950;cursor:pointer}
.pa-loc-secondary{border:1px solid #e2e8f0;background:#fff;color:#475569}
.pa-loc-primary{border:0;background:#00675f;color:#fff;box-shadow:0 12px 26px rgba(0,103,95,.20)}
.pa-loc-primary:disabled{opacity:.5;cursor:not-allowed;box-shadow:none}
.pa-loc-error{margin:0 24px 18px;color:#ef4444;font-size:12px;font-weight:800}
@media(max-width:900px){.pa-loc-overlay{padding:0}.pa-loc-modal{min-height:100dvh;margin:0;border-radius:0;display:flex;flex-direction:column}.pa-loc-grid{grid-template-columns:1fr;overflow:auto}.pa-loc-list{max-height:210px}.pa-loc-foot{position:sticky;bottom:0;display:grid}.pa-loc-actions{display:grid;grid-template-columns:1fr 1fr}.pa-loc-fee{display:grid}.pa-loc-fee label{width:100%}.pa-loc-selected-card{display:grid}.pa-loc-results{left:14px;right:14px}.pa-loc-search-wrap{padding-left:14px;padding-right:14px}.pa-loc-selected-card{margin-left:14px;margin-right:14px}.pa-loc-grid{padding-left:14px;padding-right:14px}.pa-loc-head{padding-left:14px;padding-right:14px}.pa-loc-foot{padding-left:14px;padding-right:14px}}
`;
